import React from 'react';
import { motion } from 'framer-motion';
import {
  HoverSlider,
  TextStaggerHover,
  HoverSliderImageWrap,
  HoverSliderImage,
} from './blocks/animated-slideshow';

const Gallery = () => {
  const galleryImages = [
    {
      title: "Sheet Laser Cutting",
      url: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/Flatlasersheet/flatsheet2.png"
    },
    {
      title: "Pipe Laser Cutting",
      url: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/Rolling/MVI_2544.00_00_33_05.Still012.png"
    },
    {
      title: "CNC Bending",
      url: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/CNC/MVI_2544.00_00_51_26.Still017.png"
    },
    {
      title: "Laser Welding",
      url: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/LaserWelding/welding.jpeg"
    },
    {
      title: "TIG Welding",
      url: "https://www.millerwelds.com/-/media/miller-electric/images/article-images/3/b/d/tig-it_feature.jpg?h=720&w=1280&la=en&hash=0CF9719BFD95AB8E298E664B37DF85C7"
    },
    {
      title: "MIG Welding",
      url: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/Tig&Mig/Mig-Welding.jpeg"
    },
    {
      title: "Rolling",
      url: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/Rolling/image.png"
    },
    {
      title: "Drilling & Tapping",
      url: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/Drilling/MVI_2544.00_00_24_08.Still007.png"
    }
  ];

  return (
    <section id="gallery" className="py-20 bg-white relative z-10">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          className="text-center space-y-6 mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
        >
          <div className="space-y-4">
            <p className="text-primary font-semibold uppercase tracking-wide text-sm">
              Gallery
            </p>
            <h2 className="text-4xl lg:text-5xl font-bold text-steel font-poppins leading-tight">
              Our <span className="text-primary">Work</span> <span className="text-secondary">in</span> Action
            </h2>
          </div>
          <p className="text-xl text-steel-lighter max-w-3xl mx-auto">
            Explore our state-of-the-art laser cutting facilities and precision manufacturing process.
          </p>
        </motion.div>

        {/* Desktop View (Hover Slider) */}
        <motion.div
          className="hidden lg:block"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <HoverSlider className="mx-auto max-w-7xl">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              {/* Left side - Text labels */}
              <div className="space-y-6">
                {galleryImages.map((item, index) => (
                  <div key={index} className="group">
                    <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-steel font-poppins cursor-pointer">
                      <TextStaggerHover
                        text={item.title}
                        index={index}
                      />
                    </h3>
                  </div>
                ))}
              </div>

              {/* Right side - Images */}
              <motion.div
                whileHover={{
                  scale: 1.02,
                  transition: { duration: 0.4, ease: "easeOut" }
                }}
              >
                <HoverSliderImageWrap className="aspect-[4/3] overflow-hidden">
                  {galleryImages.map((item, index) => (
                    <HoverSliderImage
                      key={index}
                      index={index}
                      imageUrl={item.url}
                      alt={item.title}
                      className="object-cover"
                    />
                  ))}
                </HoverSliderImageWrap>
              </motion.div>
            </div>
          </HoverSlider>
        </motion.div>

        {/* Mobile View (Unique Horizontal Carousel) */}
        <div className="lg:hidden relative">
          {/* Scroll Container */}
          <div className="flex overflow-x-auto snap-x snap-mandatory gap-6 pb-8 -mx-6 px-6 no-scrollbar">
            {galleryImages.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative flex-shrink-0 w-[85vw] snap-center"
              >
                {/* Card Container with Laser-Cut Shape */}
                <div
                  className="relative h-[400px] bg-steel overflow-hidden group"
                  style={{
                    clipPath: "polygon(0 0, 100% 0, 100% 85%, 85% 100%, 0 100%)"
                  }}
                >
                  {/* Image with Zoom Effect */}
                  <img
                    src={item.url}
                    alt={item.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />

                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-steel via-steel/50 to-transparent opacity-90" />

                  {/* Decorative Border Lines */}
                  <div className="absolute inset-0 border-2 border-white/10 pointer-events-none"
                    style={{ clipPath: "polygon(0 0, 100% 0, 100% 85%, 85% 100%, 0 100%)" }}
                  />
                  <div className="absolute bottom-0 right-0 w-12 h-12 border-t-2 border-l-2 border-primary/50 rotate-45 transform translate-y-6 translate-x-6" />

                  {/* Content */}
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-1 bg-gradient-to-r from-primary to-secondary" />
                      <span className="text-primary-light text-xs font-bold uppercase tracking-wider">0{index + 1}</span>
                    </div>
                    <h3 className="text-3xl font-bold text-white font-poppins leading-none mb-2">
                      {item.title.split(" ").map((word, i) => (
                        <span key={i} className="block">{word}</span>
                      ))}
                    </h3>
                    <div className="mt-4 flex items-center text-white/60 text-sm font-medium">
                      <span>View Project</span>
                      <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Swipe Indicator */}
          <div className="flex justify-center gap-2 mt-4">
            {galleryImages.map((_, index) => (
              <div
                key={index}
                className="w-1.5 h-1.5 rounded-full bg-steel/20"
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Gallery;
